import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { SEO } from '../components/SEO';
import { PortalLayout } from '../components/PortalLayout';
import { PortalSidebar } from '../components/PortalSidebar';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ScrollReveal } from '../components/ScrollReveal';
import { complianceGuides } from '../data/complianceGuides';

const guidesSidebarGroups = [
  { id: 'guides', label: 'All Guides', items: complianceGuides.map((g) => ({ id: g.id, label: `${g.number} ${g.title}`, href: `/compliance-guides/${g.id}` })) },
];

const ComplianceGuideDetail = () => {
  const { id } = useParams();
  const index = complianceGuides.findIndex((g) => g.id === id);
  const guide = complianceGuides[index];

  if (!guide) return <Navigate to="/compliance-guides" replace />;

  const nextGuide = complianceGuides[(index + 1) % complianceGuides.length];

  const guideJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: guide.title,
    description: guide.summary,
    step: guide.steps.map((s) => ({ '@type': 'HowToStep', text: s })),
  };

  return (
    <>
      <SEO title={`${guide.title} — DPDPA Compliance Guide`} description={guide.summary} path={`/compliance-guides/${guide.id}`} jsonLd={guideJsonLd} />
      <PortalLayout
        breadcrumb={<Breadcrumbs trail={[{ label: 'Compliance Guides', href: '/compliance-guides' }, { label: guide.number }]} />}
        sidebar={<PortalSidebar heading="Compliance Guides" headingHref="/compliance-guides" groups={guidesSidebarGroups} activeItemId={guide.id} defaultOpenGroupId="guides" />}
      >
        <div data-testid="compliance-guide-detail-page" className="max-w-3xl">
          <span className="font-mono text-xs text-saffron-600">{guide.number}</span>
          <h1 className="font-serif text-3xl sm:text-4xl text-navy leading-tight mt-2 mb-4">{guide.title}</h1>
          <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-10">{guide.summary}</p>

          <ol className="space-y-7" data-testid="guide-detail-steps-section">
            {guide.steps.map((step, i) => (
              <ScrollReveal key={i} delay={i * 0.05}>
                <li className="flex gap-5" data-testid={`guide-detail-step-${i}`}>
                  <span className="font-serif text-2xl text-saffron/50 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                  <p className="text-gray-700 text-base leading-relaxed pt-1">{step}</p>
                </li>
              </ScrollReveal>
            ))}
          </ol>

          <div className="bg-navy rounded-sm mt-14 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="guide-detail-next-section">
            <div>
              <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Next guide</p>
              <p className="text-white font-serif text-lg">{nextGuide.title}</p>
            </div>
            <Link to={`/compliance-guides/${nextGuide.id}`} data-testid="guide-detail-next-link" className="inline-flex items-center gap-2 bg-saffron text-white px-6 py-3 rounded-sm font-medium hover:bg-white hover:text-navy transition-colors shrink-0">
              Continue <ArrowRight size={16} />
            </Link>
          </div>

          <Link to="/compliance-guides" data-testid="guide-detail-back-link" className="inline-flex items-center gap-2 text-gray-400 hover:text-saffron-600 text-sm mt-8 transition-colors">
            <ArrowLeft size={14} /> Back to Compliance Guides
          </Link>
        </div>
      </PortalLayout>
    </>
  );
};

export default ComplianceGuideDetail;
